// src/pages/Auto.jsx - Mobile-optimized with improved responsive design + Viewer Role Support
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from "framer-motion";
import { Download, FileText, Filter, Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { autoService } from '@/services/SupabaseService';
import AutoFilters from "../components/auto/AutoFilters";
import AutoForm from "../components/auto/AutoForm";
import AutoTable from "../components/auto/AutoTable";
import StatsCard from "../components/dashboard/StatsCard";
import { useAuth } from "../contexts/AuthContext";
import ActivityService from "../services/ActivityService";

export default function AutoPage() {
  const [showForm, setShowForm] = useState(false);
  const [editingAuto, setEditingAuto] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filter state from URL search params
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || "");
  const [filters, setFilters] = useState(() => ({
    stato: searchParams.get('stato') || "all",
    cliente: searchParams.get('cliente') || "all"
  }));
  const [lastError, setLastError] = useState(null);

  // Pagination state - Mobile optimized defaults
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // NEW: Mobile UI state
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const queryClient = useQueryClient();

  // NEW: Get user profile for role-based permissions
  const { profile } = useAuth();
  const isViewer = profile?.role === 'viewer';
  const canModify = !isViewer; // viewer cannot modify anything

  // NEW: Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // NEW: Function to check numero_telaio duplicates
  const handleCheckNumeroTelaio = async (numeroTelaio, excludeAutoId = null) => {
    try {
      return await autoService.checkNumeroTelaioDuplicate(numeroTelaio, excludeAutoId);
    } catch (error) {
      console.error('Error checking numero_telaio duplicate:', error);
      return { isDuplicate: false, existingAuto: null };
    }
  };

  // Helper function to get detailed error message
  const getDetailedErrorMessage = (error, operation = "operazione") => {
    console.error(`Error in ${operation}:`, error);

    // Supabase specific errors
    if (error?.code) {
      switch (error.code) {
        case '23505':
          return `Errore: Valore duplicato. ${error.details || 'Un record con questi dati esiste già.'}`;
        case '23503':
          return `Errore: Riferimento non valido. ${error.details || 'Uno dei valori selezionati non è più disponibile.'}`;
        case '23502':
          return `Errore: Campo obbligatorio mancante. ${error.details || 'Tutti i campi richiesti devono essere compilati.'}`;
        case '42501':
          return `Errore: Permessi insufficienti. ${error.details || 'Non hai i permessi per questa operazione.'}`;
        case 'PGRST116':
          return `Errore: Record non trovato. ${error.details || 'Il record che stai cercando di modificare non esiste più.'}`;
        default:
          return `Errore database (${error.code}): ${error.message || error.details || 'Errore sconosciuto'}`;
      }
    }

    // Network errors
    if (error?.name === 'NetworkError' || error?.message?.includes('fetch')) {
      return 'Errore di connessione: Controlla la tua connessione internet e riprova.';
    }

    // Validation errors
    if (error?.message?.includes('validation') || error?.message?.includes('invalid')) {
      return `Errore di validazione: ${error.message}`;
    }

    // Generic error with details
    if (error?.message) {
      return `Errore durante ${operation}: ${error.message}`;
    }

    // Fallback
    return `Errore sconosciuto durante ${operation}. Riprova o contatta l'assistenza.`;
  };

  // Load all necessary data
  const { data: auto = [], isLoading: autoLoading } = useQuery({
    queryKey: ['auto'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('auto')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  const { data: clienti = [], isLoading: clientiLoading } = useQuery({
    queryKey: ['clienti'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clienti')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: stati = [], isLoading: statiLoading } = useQuery({
    queryKey: ['stati'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stati')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: tettoie = [], isLoading: tettoieLoading } = useQuery({
    queryKey: ['tettoie'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tettoie')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: sezioni = [], isLoading: sezioniLoading } = useQuery({
    queryKey: ['sezioni'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sezioni')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: campate = [], isLoading: campateLoading } = useQuery({
    queryKey: ['campate'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campate')
        .select('*')
        .order('nome');
      if (error) throw error;
      return data || [];
    }
  });

  // ENHANCED: Create mutation with better error handling (only available for non-viewers)
  const createAutoMutation = useMutation({
    mutationFn: async (autoData) => {
      if (isViewer) {
        throw new Error("Non hai i permessi per creare nuove auto.");
      }

      console.log('🚗 Creating new auto:', autoData);
      setLastError(null);

      const { data, error } = await supabase
        .from('auto')
        .insert(autoData)
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating auto:', error);
        throw error;
      }

      console.log('✅ Auto created successfully:', data);

      // Log the CREATE activity
      const identifier = data.targa || `Auto ID: ${data.id}`;
      console.log('📝 Logging CREATE activity for:', identifier);

      try {
        const logResult = await ActivityService.logCreate('auto', data, identifier);
        console.log('📝 Activity log result:', logResult);
      } catch (logError) {
        console.warn('⚠️ Failed to log activity, but auto was created successfully:', logError);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      setLastError(null);
    },
    onError: (error) => {
      console.error('💥 Create mutation failed:', error);
      const errorMessage = getDetailedErrorMessage(error, "creazione auto");
      setLastError(errorMessage);
    }
  });

  // ENHANCED: Update mutation with better error handling (only available for non-viewers)
  const updateAutoMutation = useMutation({
    mutationFn: async ({ id, data: updateData, oldData }) => {
      if (isViewer) {
        throw new Error("Non hai i permessi per modificare le auto.");
      }

      console.log('🔄 Updating auto:', { id, updateData, oldData });
      setLastError(null);

      const { data: result, error } = await supabase
        .from('auto')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating auto:', error);
        throw error;
      }

      console.log('✅ Auto updated successfully:', result);

      // Enhanced field mappings for better change descriptions
      const fieldMappings = {
        targa: 'Targa',
        marca: 'Marca',
        modello: 'Modello',
        numero_telaio: 'Numero Telaio',
        cliente_id: `Cliente (${getClienteName(oldData.cliente_id)} → ${getClienteName(result.cliente_id)})`,
        stato_id: `Stato (${getStatoName(oldData.stato_id)} → ${getStatoName(result.stato_id)})`,
        campata_id: `Posizione (${getCampataFullName(oldData.campata_id)} → ${getCampataFullName(result.campata_id)})`,
        data_ingresso: 'Data Ingresso',
        data_uscita: 'Data Uscita',
        note: 'Note'
      };

      // Log the UPDATE activity with enhanced field mappings
      const identifier = result.targa || oldData.targa || `Auto ID: ${id}`;
      console.log('📝 Logging UPDATE activity for:', identifier);

      try {
        const logResult = await ActivityService.logUpdate('auto', id, oldData, result, identifier, fieldMappings);
        console.log('📝 Activity log result:', logResult);
      } catch (logError) {
        console.warn('⚠️ Failed to log activity, but auto was updated successfully:', logError);
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      setLastError(null);
    },
    onError: (error) => {
      console.error('💥 Update mutation failed:', error);
      const errorMessage = getDetailedErrorMessage(error, "aggiornamento auto");
      setLastError(errorMessage);
    }
  });

  // ENHANCED: Delete mutation with double-click prevention
  const deleteAutoMutation = useMutation({
    mutationFn: async (autoItem) => {
      if (isViewer) {
        throw new Error("Non hai i permessi per eliminare le auto.");
      }

      console.log('🗑️ Archiving auto:', autoItem);
      setLastError(null);

      // Step 1: Copy to archive table
      const archiveData = {
        ...autoItem,
        archived_at: new Date().toISOString(),
        // Remove the original id to let the archive table generate a new one
        id: undefined
      };

      const { error: archiveError } = await supabase
        .from('autoarchivio')
        .insert(archiveData);

      if (archiveError) {
        console.error('❌ Archive error:', archiveError);
        throw new Error(`Errore durante l'archiviazione: ${archiveError.message}`);
      }

      // Step 2: Delete from auto table
      const { error: deleteError } = await supabase
        .from('auto')
        .delete()
        .eq('id', autoItem.id);

      if (deleteError) {
        console.error('❌ Delete error:', deleteError);
        throw new Error(`Errore durante l'eliminazione: ${deleteError.message}`);
      }

      console.log('✅ Auto archived successfully');

      // Log the ARCHIVE activity
      const identifier = autoItem.targa || `Auto ID: ${autoItem.id}`;
      console.log('📝 Logging ARCHIVE activity for:', identifier);

      try {
        const logResult = await ActivityService.logArchive('auto', autoItem.id, autoItem, identifier);
        console.log('📝 Activity log result:', logResult);
      } catch (logError) {
        console.warn('⚠️ Failed to log activity, but auto was archived successfully:', logError);
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auto'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      setLastError(null);
    },
    onError: (error) => {
      console.error('💥 Delete mutation error:', error);
      const errorMessage = getDetailedErrorMessage(error, "archiviazione auto");
      setLastError(errorMessage);
    }
  });

  const isLoading = autoLoading || clientiLoading || statiLoading ||
    tettoieLoading || sezioniLoading || campateLoading;

  // NEW: Enhanced filtering logic that works across ALL data
  const filteredAuto = useMemo(() => {
    return auto.filter(a => {
      const matchesSearch = !searchTerm ||
        a.targa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.modello?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.numero_telaio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.note?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStato = filters.stato === "all" || a.stato_id === filters.stato;
      const matchesCliente = filters.cliente === "all" || a.cliente_id === filters.cliente;

      return matchesSearch && matchesStato && matchesCliente;
    });
  }, [auto, searchTerm, filters]);

  // NEW: Mobile-optimized pagination calculations
  const mobilePagination = isMobile && filteredAuto.length > 20;
  const effectivePageSize = mobilePagination ? Math.min(pageSize, 20) : pageSize;
  const totalPages = Math.ceil(filteredAuto.length / effectivePageSize);
  const startIndex = (currentPage - 1) * effectivePageSize;
  const endIndex = startIndex + effectivePageSize;
  const paginatedAuto = filteredAuto.slice(startIndex, endIndex);

  // NEW: Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, pageSize]);

  // NEW: Ensure current page is valid when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Calculate stats by state (based on ALL auto, not filtered)
  const getStatsData = () => {
    const stateCounts = {};

    // Initialize counts for all states
    stati.forEach(stato => {
      stateCounts[stato.id] = {
        name: stato.nome,
        count: 0
      };
    });

    // Count cars by state (using ALL auto data)
    auto.forEach(car => {
      if (car.stato_id && stateCounts[car.stato_id]) {
        stateCounts[car.stato_id].count++;
      }
    });

    return Object.values(stateCounts);
  };

  const statsData = getStatsData();

  // Colors for different states (cycling through available colors)
  const stateColors = ['blue', 'green', 'orange', 'purple', 'red', 'yellow', 'pink', 'indigo'];

  // NEW: Mobile-optimized pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top on mobile when changing pages
    if (isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    const mobileMaxPageSize = isMobile ? 20 : newPageSize;
    setPageSize(mobileMaxPageSize);
    setCurrentPage(1);
  };

  // ENHANCED: Submit handler with better error handling (disabled for viewers)
  const handleSubmit = async (autoData) => {
    if (isViewer) {
      setLastError("Non hai i permessi per modificare le auto.");
      return;
    }

    try {
      console.log('📤 Submitting auto data:', { autoData, editingAuto });
      setLastError(null);

      if (editingAuto) {
        // For updates, pass the old data for comparison
        await updateAutoMutation.mutateAsync({
          id: editingAuto.id,
          data: autoData,
          oldData: editingAuto
        });
        console.log('✅ Auto updated successfully');
      } else {
        // For creates
        await createAutoMutation.mutateAsync(autoData);
        console.log('✅ Auto created successfully');
      }

      setShowForm(false);
      setEditingAuto(null);
    } catch (error) {
      console.error("💥 Error in handleSubmit:", error);
    }
  };

  const handleEdit = (autoItem) => {
    if (isViewer) {
      setLastError("Non hai i permessi per modificare le auto.");
      return;
    }

    console.log('✏️ Navigating to edit auto:', autoItem);
    // Build query params to preserve filter state
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (filters.stato !== 'all') params.set('stato', filters.stato);
    if (filters.cliente !== 'all') params.set('cliente', filters.cliente);
    const queryString = params.toString();
    navigate(`/auto/edit/${autoItem.id}${queryString ? `?${queryString}` : ''}`);
  };

  // FIXED: Handler with double-click prevention
  const handleDelete = async (autoItem) => {
    if (isViewer) {
      setLastError("Non hai i permessi per eliminare le auto.");
      return;
    }

    // ✅ PROTECTION: Prevent double-click by checking if mutation is already running
    if (deleteAutoMutation.isPending) {
      console.log('⚠️ Archive already in progress, ignoring duplicate click');
      return;
    }

    try {
      console.log('🗑️ Deleting auto:', autoItem);
      setLastError(null);
      await deleteAutoMutation.mutateAsync(autoItem);
      console.log('✅ Auto deleted successfully');
    } catch (error) {
      console.error("💥 Error in handleDelete:", error);
    }
  };

  const getClienteName = (clienteId) => {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente ? `${cliente.nome} ${cliente.cognome}` : "Cliente sconosciuto";
  };

  const getStatoName = (statoId) => {
    const stato = stati.find(s => s.id === statoId);
    return stato ? stato.nome : "Stato sconosciuto";
  };

  const getCampataFullName = (campataId) => {
    if (!campataId) return "N/D";
    const campata = campate.find(c => c.id === campataId);
    if (!campata) return "N/D";
    const sezione = sezioni.find(s => s.id === campata.sezione_id);
    if (!sezione) return campata.nome;
    const tettoia = tettoie.find(t => t.id === sezione.tettoia_id);
    if (!tettoia) return `${sezione.nome} / ${campata.nome}`;
    return `${tettoia.nome} / ${sezione.nome} / ${campata.nome}`;
  };

  // NEW: Enhanced CSV Export function (exports filtered data) - DISABLED for viewers
  const exportToCSV = () => {
    if (isViewer) {
      setLastError("Non hai i permessi per esportare i dati.");
      return;
    }

    const headers = [
      'Targa',
      'Marca',
      'Modello',
      'Numero Telaio',
      'Cliente',
      'Data Ingresso',
      'Data Uscita',
      'Stato',
      'Posizione',
      'Km',
      'Note'
    ];

    const csvData = filteredAuto.map(autoItem => [
      autoItem.targa || '',
      autoItem.marca || '',
      autoItem.modello || '',
      autoItem.numero_telaio || '',
      getClienteName(autoItem.cliente_id),
      autoItem.data_ingresso ? new Date(autoItem.data_ingresso).toLocaleDateString('it-IT') : '',
      autoItem.data_uscita ? new Date(autoItem.data_uscita).toLocaleDateString('it-IT') : '',
      getStatoName(autoItem.stato_id),
      getCampataFullName(autoItem.campata_id),
      autoItem.km ?? 0,
      autoItem.note || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `auto-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // NEW: Check if any filters are active
  const hasActiveFilters = searchTerm || filters.stato !== "all" || filters.cliente !== "all";

  return (
    <div className={`${isMobile ? 'p-3' : 'p-4 md:p-8'} min-h-screen`}>
      <div className="max-w-7xl mx-auto">
        {/* MOBILE-OPTIMIZED: Header Section */}
        <div className={`${isMobile ? 'space-y-4' : 'flex flex-col md:flex-row justify-between items-start md:items-center'} mb-6`}>
          <div className={isMobile ? 'text-center' : ''}>
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-slate-800 mb-2`}>
              Gestione Auto
              {isViewer && (
                <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-md">
                  Solo Visualizzazione
                </span>
              )}
            </h1>
            <p className={`text-slate-600 ${isMobile ? 'text-sm' : ''}`}>
              {isViewer ? 'Visualizza tutte le auto nel parcheggio' : 'Gestisci tutte le auto nel parcheggio'}
              {filteredAuto.length !== auto.length && (
                <span className={`ml-2 ${isMobile ? 'block mt-1' : 'text-sm'} text-blue-600`}>
                  ({filteredAuto.length} di {auto.length} auto visualizzate)
                </span>
              )}
            </p>
          </div>

          {/* MOBILE-OPTIMIZED: Action Buttons - CONDITIONAL for non-viewers */}
          {canModify && (
            <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center gap-3'}`}>
              {!isMobile && (
                <Button
                  onClick={exportToCSV}
                  variant="outline"
                  className="bg-white hover:bg-slate-50 border-slate-300 text-slate-700 shadow-sm"
                  disabled={filteredAuto.length === 0}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Esporta CSV {filteredAuto.length !== auto.length && `(${filteredAuto.length})`}
                </Button>
              )}
              <Button
                onClick={() => {
                  setShowForm(!showForm);
                  setLastError(null);
                }}
                className={`bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg ${isMobile ? 'w-full' : ''}`}
                disabled={createAutoMutation.isPending}
              >
                <Plus className="w-5 h-5 mr-2" />
                {isMobile ? 'Aggiungi Auto' : 'Nuova Auto'}
              </Button>
            </div>
          )}
        </div>

        {/* ENHANCED: Error display */}
        {lastError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 bg-red-50 border border-red-200 rounded-lg ${isMobile ? 'mx-1' : ''}`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Errore nell'operazione
                </h3>
                <p className={`mt-1 ${isMobile ? 'text-xs' : 'text-sm'} text-red-700`}>{lastError}</p>
                <div className="mt-2">
                  <Button
                    onClick={() => setLastError(null)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Chiudi
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* MOBILE-OPTIMIZED: State Stats Cards */}
        <div className="mb-6">
          <div className={`grid ${isMobile ? 'grid-cols-2 gap-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6'}`}>
            {statsData.map((stat, index) => (
              <StatsCard
                key={stat.name}
                title={stat.name}
                value={stat.count}
                icon={FileText}
                color={stateColors[index % stateColors.length]}
                isLoading={isLoading}
                compact={isMobile}
              />
            ))}
          </div>
        </div>

        {/* CONDITIONAL: Auto Form - only for non-viewers */}
        <AnimatePresence>
          {showForm && canModify && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6"
            >
              <AutoForm
                auto={null}
                clienti={clienti}
                tettoie={tettoie}
                sezioni={sezioni}
                campate={campate}
                stati={stati}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setShowForm(false);
                  setEditingAuto(null);
                  setLastError(null);
                }}
                isLoading={createAutoMutation.isPending || updateAutoMutation.isPending}
                onCheckNumeroTelaio={handleCheckNumeroTelaio}
                isMobile={isMobile}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* MOBILE-OPTIMIZED: Search and Filters */}
        <div className={`${isMobile ? 'space-y-3' : 'flex flex-col md:flex-row gap-4'} mb-4`}>
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <Input
                placeholder={isMobile ? "Cerca auto..." : "Cerca per targa, marca, modello, telaio..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 glass-effect border-0 shadow-lg"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  aria-label="Cancella ricerca"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* MOBILE-OPTIMIZED: Filter Section */}
          {isMobile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outline"
                  className="flex-1 justify-between"
                >
                  <div className="flex items-center">
                    <Filter className="w-4 h-4 mr-2" />
                    Filtri
                    {hasActiveFilters && (
                      <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                        Attivi
                      </span>
                    )}
                  </div>
                  <motion.div
                    animate={{ rotate: showFilters ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    ▼
                  </motion.div>
                </Button>

                {/* Mobile Export Button - CONDITIONAL for non-viewers */}
                {canModify && (
                  <Button
                    onClick={exportToCSV}
                    variant="outline"
                    size="sm"
                    className="ml-2 px-3"
                    disabled={filteredAuto.length === 0}
                    title="Esporta CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-slate-50 p-3 rounded-lg border">
                      <AutoFilters
                        filters={filters}
                        setFilters={setFilters}
                        clienti={clienti}
                        stati={stati}
                        isMobile={isMobile}
                      />

                      {hasActiveFilters && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <Button
                            onClick={() => {
                              setSearchTerm("");
                              setFilters({ stato: "all", cliente: "all" });
                            }}
                            variant="outline"
                            size="sm"
                            className="w-full text-slate-600"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancella filtri
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="md:w-auto w-full">
              <AutoFilters
                filters={filters}
                setFilters={setFilters}
                clienti={clienti}
                stati={stati}
                isMobile={isMobile}
              />
            </div>
          )}
        </div>

        {/* MOBILE-OPTIMIZED: Pagination Controls - Top */}
        {totalPages > 1 && (
          <div className={`mb-4 ${isMobile ? 'px-1' : ''}`}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={effectivePageSize}
              totalItems={filteredAuto.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={isMobile ? [10, 20] : [10, 20, 50, 100]}
              showPageSizeSelector={!isMobile}
              showInfo={!isMobile}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* MOBILE-OPTIMIZED: Auto Table with Paginated Data - CONDITIONAL actions for viewers */}
        <div className={isMobile ? 'overflow-hidden' : ''}>
          <AutoTable
            auto={paginatedAuto}
            getClienteName={getClienteName}
            getStatoName={getStatoName}
            getCampataFullName={getCampataFullName}
            onEdit={canModify ? handleEdit : undefined}
            onDelete={canModify ? handleDelete : undefined}
            isLoading={isLoading}
            deleteLoading={deleteAutoMutation.isPending}
            stati={stati}
            showPresenceStatus={false}
            isMobile={isMobile}
            isViewer={isViewer} // Pass viewer status to the table
          />
        </div>

        {/* MOBILE-OPTIMIZED: Pagination Controls - Bottom */}
        {totalPages > 1 && (
          <div className={`mt-6 ${isMobile ? 'px-1' : ''}`}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={effectivePageSize}
              totalItems={filteredAuto.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={isMobile ? [10, 20] : [10, 20, 50, 100]}
              showPageSizeSelector={false}
              showInfo={false}
              isMobile={isMobile}
            />
          </div>
        )}

        {/* MOBILE-OPTIMIZED: Quick Stats Summary for Mobile */}
        {isMobile && filteredAuto.length > 0 && (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg border text-center">
            <p className="text-sm text-slate-600">
              Visualizzando <span className="font-semibold text-slate-800">{paginatedAuto.length}</span> di{' '}
              <span className="font-semibold text-slate-800">{filteredAuto.length}</span> auto
              {totalPages > 1 && (
                <span className="block mt-1">
                  Pagina {currentPage} di {totalPages}
                </span>
              )}
            </p>
          </div>
        )}

        {/* MOBILE-OPTIMIZED: No results message */}
        {filteredAuto.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`text-center ${isMobile ? 'py-8' : 'py-12'}`}
          >
            <div className="text-slate-400 mb-4">
              <svg className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} mx-auto`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
              </svg>
            </div>
            <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-slate-600 mb-2`}>
              Nessuna auto trovata
            </h3>
            <p className={`text-slate-500 mb-4 ${isMobile ? 'text-sm px-4' : ''}`}>
              {hasActiveFilters
                ? "Prova a modificare i filtri di ricerca"
                : "Non ci sono auto nel sistema"
              }
            </p>
            {hasActiveFilters && (
              <Button
                onClick={() => {
                  setSearchTerm("");
                  setFilters({ stato: "all", cliente: "all" });
                }}
                variant="outline"
                className={`text-slate-600 ${isMobile ? 'text-sm' : ''}`}
              >
                <X className="w-4 h-4 mr-2" />
                Cancella filtri
              </Button>
            )}
          </motion.div>
        )}

        {/* MOBILE-OPTIMIZED: Bottom spacing */}
        <div className={isMobile ? 'h-16' : 'h-8'}></div>
      </div>
    </div>
  );
}